import React, { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/dialog/dialog";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RemoveIcon, CheckIcon } from "@/icons";
import { PokemonBoxData, InventoryPokemonsOutput } from "types/web-app";
import cn from "classnames";

interface MoveToBoxDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPokemons: InventoryPokemonsOutput["player_pokemons"];
  onMove: (targetBoxId: string | null) => Promise<void>;
  boxes: PokemonBoxData[];
}

export const MoveToBoxDialog = ({
  isOpen,
  onClose,
  selectedPokemons,
  onMove,
  boxes,
}: MoveToBoxDialogProps) => {
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null); // null means "Main Inventory" (default/no box)
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedTarget(null); // Default to Main Inventory or reset
    }
  }, [isOpen]);

  const handleMove = async () => {
    setIsMoving(true);
    try {
      await onMove(selectedTarget);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsMoving(false);
    }
  };

  const BoxItem = ({
    box,
    isSelected,
    onClick,
  }: {
    box: PokemonBoxData | null;
    isSelected: boolean;
    onClick: () => void;
  }) => {
    const isMain = box === null;
    const color = isMain ? "#718096" : box.color || "#ccc";
    const name = isMain ? "Main Inventory" : box.name;
    const icon = isMain ? "box" : box.icon;
    const count = isMain ? "" : `${box.pokemon_count || 0} items`;

    return (
      <div
        onClick={onClick}
        className={cn(
          "relative h-16 border-2 rounded-lg p-2 flex flex-col justify-between cursor-pointer transition-all active:scale-95",
          {
            "border-pk-orange-4 scale-[1.02] shadow-cartoon": isSelected,
            "border-pk-gray-4 hover:border-white/50": !isSelected,
          }
        )}
        style={{
          backgroundColor: color,
        }}
      >
        {isSelected && (
            <div className="absolute -top-2 -right-2 bg-pk-orange-4 rounded-full p-1 border border-white">
                <CheckIcon width={12} height={12} className="invert" />
            </div>
        )}
        
        <div className="flex items-start justify-between">
            <span className="font-bold text-white text-[10px] truncate pr-4 drop-shadow-md">
            {name}
            </span>
        </div>
        
        <div className="flex items-end justify-between">
          <span className="text-[10px] text-white/70 font-medium drop-shadow-sm">
            {count}
          </span>
          {isMain ? (
              <div className="w-8 h-8 opacity-80 flex items-center justify-center">
                  <span className="text-xl">🎒</span>
              </div>
          ) : (
            <img
                src={`/assets/icons/boxes/${icon}.svg`}
                className="w-8 h-8 opacity-80 fill-white stroke-white drop-shadow-sm"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <StandardFrame
        variant="purple"
        className="w-[90%] max-w-md max-h-[90vh] flex flex-col gap-4 overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-2">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider">
            Move to Box
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <RemoveIcon width={32} height={32} className="invert" />
          </button>
        </div>

        <div className="text-sm text-white/80 px-1">
            Moving <span className="font-bold text-pk-orange-3">{selectedPokemons.length}</span> Pokémon to:
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-2 p-1">
          {/* Main Inventory Option */}
          <BoxItem
            box={null}
            isSelected={selectedTarget === null}
            onClick={() => setSelectedTarget(null)}
          />

          {/* User Boxes */}
          {boxes.map((box) => (
            <BoxItem
              key={box.id}
              box={box}
              isSelected={selectedTarget === box.id}
              onClick={() => setSelectedTarget(box.id)}
            />
          ))}
        </div>

        <div className="flex gap-2 mt-2 pt-2 border-t border-white/10">
          <SimpleButton onClick={onClose} variant="gray" className="flex-1">
            Cancel
          </SimpleButton>
          <SimpleButton
            onClick={handleMove}
            variant="green"
            className="flex-1"
            disabled={isMoving}
          >
            {isMoving ? (
              "Moving..."
            ) : (
              <div className="flex items-center justify-center gap-2">
                Move Items
              </div>
            )}
          </SimpleButton>
        </div>
      </StandardFrame>
    </Dialog>
  );
};
